import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

// Types
export interface Event {
  id: string;
  code: string;
  name: string;
  emoji: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  expires_at: string | null;
  seasonal_prompt: string | null;
  created_at: string;
  updated_at: string;
}

export interface Genre {
  id: string;
  code: string;
  name: string;
  emoji: string;
  description: string | null;
  suno_style: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Vocal {
  id: string;
  code: string;
  name: string;
  emoji: string;
  description: string | null;
  suno_style: string | null;
  sort_order: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface TelegramUser {
  id: string;
  telegram_id: number;
  username: string | null;
  first_name: string | null;
  last_name: string | null;
  wow_hits_balance: number;
  total_generations: number;
  referral_count: number;
  referred_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface Generation {
  id: string;
  user_id: string;
  event_id: string | null;
  genre_id: string | null;
  story: string;
  lyrics: string | null;
  music_url: string | null;
  demo_url: string | null;
  full_music_url: string | null;
  full_price_rub: number | null;
  is_full_purchased: boolean | null;
  status: 'pending' | 'generating_text' | 'text_ready' | 'generating_music' | 'demo_ready' | 'completed' | 'failed';
  wow_hits_used: number;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  telegram_users?: TelegramUser;
  events?: Event;
  genres?: Genre;
}

export interface Prompt {
  id: string;
  name: string;
  type: 'claude' | 'suno';
  prompt_template: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Feedback {
  id: string;
  user_id: string;
  generation_id: string | null;
  rating: number | null;
  liked: string[] | null;
  improve: string[] | null;
  comment: string | null;
  created_at: string;
  telegram_users?: TelegramUser;
}

export interface Transaction {
  id: string;
  user_id: string;
  amount: number;
  price_rub: number;
  package_name: string;
  payment_id: string | null;
  status: 'pending' | 'completed' | 'failed' | 'refunded';
  created_at: string;
  telegram_users?: TelegramUser;
}

// Dashboard stats
export function useStats() {
  return useQuery({
    queryKey: ['admin-stats'],
    queryFn: async () => {
      const [generations, users, transactions] = await Promise.all([
        supabase.from('generations').select('id, status', { count: 'exact' }),
        supabase.from('telegram_users').select('id', { count: 'exact' }),
        supabase.from('transactions').select('price_rub, status').eq('status', 'completed'),
      ]);

      const totalRevenue = (transactions.data || []).reduce((sum, t) => sum + t.price_rub, 0);
      const completedGenerations = (generations.data || []).filter(g => g.status === 'completed').length;
      const conversionRate = generations.count ? Math.round((completedGenerations / generations.count) * 100) : 0;

      return {
        totalHits: generations.count || 0,
        activeUsers: users.count || 0,
        monthlyRevenue: totalRevenue,
        conversionRate,
      };
    },
  });
}

// Events
export function useEvents() {
  return useQuery({
    queryKey: ['events'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('events')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return data as Event[];
    },
  });
}

export function useUpdateEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Event> & { id: string }) => {
      const { error } = await supabase.from('events').update(updates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['events'] }),
  });
}

export function useDeleteEvent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('events').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['events'] }),
  });
}

// Genres
export function useGenres() {
  return useQuery({
    queryKey: ['genres'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('genres')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return data as Genre[];
    },
  });
}

export function useUpdateGenre() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Genre> & { id: string }) => {
      const { error } = await supabase.from('genres').update(updates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['genres'] }),
  });
}

export function useDeleteGenre() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('genres').delete().eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['genres'] }),
  });
}

// Vocals
export function useVocals() {
  return useQuery({
    queryKey: ['vocals'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('vocals')
        .select('*')
        .order('sort_order');
      if (error) throw error;
      return data as Vocal[];
    },
  });
}

export function useUpdateVocal() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Vocal> & { id: string }) => {
      const { error } = await supabase.from('vocals').update(updates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['vocals'] }),
  });
}

// Users
export function useUsers() {
  return useQuery({
    queryKey: ['telegram-users'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('telegram_users')
        .select('*')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data as TelegramUser[];
    },
  });
}

// Top Referrers
export function useTopReferrers(limit = 10) {
  return useQuery({
    queryKey: ['top-referrers', limit],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('telegram_users')
        .select('*')
        .gt('referral_count', 0)
        .order('referral_count', { ascending: false })
        .limit(limit);
      if (error) throw error;
      return data as TelegramUser[];
    },
  });
}

// Referral Stats
export function useReferralStats() {
  return useQuery({
    queryKey: ['referral-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('telegram_users')
        .select('referral_count, referred_by');
      if (error) throw error;
      
      const totalReferrals = data.reduce((sum, u) => sum + (u.referral_count || 0), 0);
      const usersWithReferrals = data.filter(u => u.referral_count > 0).length;
      const referredUsers = data.filter(u => u.referred_by !== null).length;
      
      return {
        totalReferrals,
        usersWithReferrals,
        referredUsers,
      };
    },
  });
}

// Generations
export function useGenerations() {
  return useQuery({
    queryKey: ['generations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('generations')
        .select(`
          *,
          telegram_users(*),
          events(*),
          genres(*)
        `)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return data as Generation[];
    },
  });
}

// Prompts
export function usePrompts() {
  return useQuery({
    queryKey: ['prompts'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('prompts')
        .select('*')
        .order('name');
      if (error) throw error;
      return data as Prompt[];
    },
  });
}

export function useUpdatePrompt() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...updates }: Partial<Prompt> & { id: string }) => {
      const { error } = await supabase.from('prompts').update(updates).eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['prompts'] }),
  });
}

// Transactions
export function useTransactions() {
  return useQuery({
    queryKey: ['transactions'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('transactions')
        .select(`
          *,
          telegram_users(*)
        `)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return data as Transaction[];
    },
  });
}

// Full Track Purchase Stats
export function useFullTrackStats() {
  return useQuery({
    queryKey: ['full-track-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('generations')
        .select('id, is_full_purchased, full_price_rub, demo_url, full_music_url, created_at, status');
      
      if (error) throw error;
      
      // Filter for generations that have demo (demo_url exists or status is demo_ready/completed)
      const withDemo = (data || []).filter(g => g.demo_url || g.status === 'completed');
      const purchased = (data || []).filter(g => g.is_full_purchased === true);
      const totalRevenue = purchased.reduce((sum, g) => sum + (g.full_price_rub || 449), 0);
      const conversionRate = withDemo.length > 0 
        ? Math.round((purchased.length / withDemo.length) * 100) 
        : 0;
      
      // Get recent purchases (last 10)
      const recentPurchases = purchased
        .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
        .slice(0, 10);

      // Today's stats
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const todayPurchases = purchased.filter(g => new Date(g.created_at) >= today);
      const todayRevenue = todayPurchases.reduce((sum, g) => sum + (g.full_price_rub || 449), 0);
      
      return {
        totalDemos: withDemo.length,
        totalPurchases: purchased.length,
        totalRevenue,
        conversionRate,
        recentPurchases,
        todayPurchases: todayPurchases.length,
        todayRevenue,
      };
    },
  });
}

// Abusers - users with many generations but no purchases
export function useAbusers(limit = 10) {
  return useQuery({
    queryKey: ['abusers', limit],
    queryFn: async () => {
      // Get users sorted by total_generations desc
      const { data: users, error: usersError } = await supabase
        .from('telegram_users')
        .select('id, telegram_id, username, first_name, last_name, total_generations, wow_hits_balance, created_at')
        .gt('total_generations', 0)
        .order('total_generations', { ascending: false })
        .limit(50);
      
      if (usersError) throw usersError;
      if (!users?.length) return [];

      // Get purchase counts per user
      const userIds = users.map(u => u.id);
      const { data: purchases, error: purchasesError } = await supabase
        .from('generations')
        .select('user_id, is_full_purchased')
        .in('user_id', userIds)
        .eq('is_full_purchased', true);
      
      if (purchasesError) throw purchasesError;

      const purchaseCounts: Record<string, number> = {};
      (purchases || []).forEach(p => {
        purchaseCounts[p.user_id] = (purchaseCounts[p.user_id] || 0) + 1;
      });

      // Also check transactions
      const { data: transactions } = await supabase
        .from('transactions')
        .select('user_id')
        .in('user_id', userIds)
        .eq('status', 'completed');

      const hasTransaction = new Set((transactions || []).map(t => t.user_id));

      return users
        .map(u => ({
          ...u,
          purchases: purchaseCounts[u.id] || 0,
          has_any_payment: hasTransaction.has(u.id),
          abuse_score: u.total_generations - (purchaseCounts[u.id] || 0) * 3,
        }))
        .filter(u => !u.has_any_payment && u.total_generations >= 3)
        .sort((a, b) => b.abuse_score - a.abuse_score)
        .slice(0, limit);
    },
  });
}

// Feedback
export function useFeedback() {
  return useQuery({
    queryKey: ['feedback'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feedback')
        .select(`
          *,
          telegram_users:user_id(id, username, first_name, last_name)
        `)
        .order('created_at', { ascending: false })
        .limit(100);
      if (error) throw error;
      return data as Feedback[];
    },
  });
}

export function useFeedbackStats() {
  return useQuery({
    queryKey: ['feedback-stats'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('feedback')
        .select('rating, liked, improve');
      
      if (error) throw error;
      
      const totalFeedback = data?.length || 0;
      const ratingsSum = (data || []).reduce((sum, f) => sum + (f.rating || 0), 0);
      const avgRating = totalFeedback > 0 ? ratingsSum / totalFeedback : 0;
      
      // Rating distribution
      const ratingCounts: Record<number, number> = { 1: 0, 2: 0, 3: 0, 4: 0, 5: 0 };
      (data || []).forEach(f => {
        if (f.rating && f.rating >= 1 && f.rating <= 5) {
          ratingCounts[f.rating]++;
        }
      });
      const ratingDistribution = [1, 2, 3, 4, 5].map(rating => ({
        rating,
        count: ratingCounts[rating],
      }));
      
      // Satisfied/unsatisfied percentages
      const satisfied = (data || []).filter(f => f.rating && f.rating >= 4).length;
      const unsatisfied = (data || []).filter(f => f.rating && f.rating <= 2).length;
      const satisfiedPercent = totalFeedback > 0 ? Math.round((satisfied / totalFeedback) * 100) : 0;
      const unsatisfiedPercent = totalFeedback > 0 ? Math.round((unsatisfied / totalFeedback) * 100) : 0;
      
      // Improve stats
      const improveCounts: Record<string, number> = {};
      (data || []).forEach(f => {
        (f.improve || []).forEach((item: string) => {
          improveCounts[item] = (improveCounts[item] || 0) + 1;
        });
      });
      const improveStats = Object.entries(improveCounts)
        .map(([item, count]) => ({ item, count }))
        .sort((a, b) => b.count - a.count);
      
      // Liked stats
      const likedCounts: Record<string, number> = {};
      (data || []).forEach(f => {
        (f.liked || []).forEach((item: string) => {
          likedCounts[item] = (likedCounts[item] || 0) + 1;
        });
      });
      const likedStats = Object.entries(likedCounts)
        .map(([item, count]) => ({ item, count }))
        .sort((a, b) => b.count - a.count);
      
      return {
        totalFeedback,
        avgRating,
        ratingDistribution,
        satisfiedPercent,
        unsatisfiedPercent,
        improveStats,
        likedStats,
      };
    },
  });
}

// Refund Stats
export function useRefundStats() {
  return useQuery({
    queryKey: ['refund-stats'],
    queryFn: async () => {
      // Get all failed generations that had hits used (= refunds)
      const { data: failedGens, error: genError } = await supabase
        .from('generations')
        .select('id, wow_hits_used, created_at, user_id, telegram_users(username, first_name)')
        .eq('status', 'failed')
        .gt('wow_hits_used', 0)
        .order('created_at', { ascending: false });
      
      if (genError) throw genError;

      // Get explicit refund logs from generation_timings
      const { data: refundLogs, error: logError } = await supabase
        .from('generation_timings')
        .select('id, generation_id, timestamp, metadata')
        .eq('stage', 'hits_refunded')
        .order('timestamp', { ascending: false });
      
      if (logError) throw logError;

      const refundedGenIds = new Set((refundLogs || []).map(l => l.generation_id));
      const totalRefundedHits = (failedGens || []).reduce((sum, g) => sum + g.wow_hits_used, 0);
      const totalRefunds = failedGens?.length || 0;
      const autoRefunds = refundLogs?.length || 0;
      const manualRefunds = totalRefunds - autoRefunds;

      // Retry stats
      const { data: retryLogs } = await supabase
        .from('generation_timings')
        .select('id, generation_id')
        .eq('stage', 'suno_retry');
      
      const totalRetries = retryLogs?.length || 0;

      return {
        totalRefunds,
        totalRefundedHits,
        autoRefunds,
        manualRefunds,
        totalRetries,
        refundedGenerations: (failedGens || []).map(g => ({
          ...g,
          wasAutoRefunded: refundedGenIds.has(g.id),
          userName: g.telegram_users?.username 
            ? `@${g.telegram_users.username}` 
            : g.telegram_users?.first_name || 'Пользователь',
        })),
      };
    },
  });
}
